import React, { useState, useMemo } from 'react'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useHistory, useParams, useLocation } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { v4 as uuid } from 'uuid'
import { isEmpty, orderBy } from 'lodash'

import Modifiers from 'components/ModifierGroups/Form/Drawers/Modifiers'
import Modal from 'components/Modal'
import TextBox from 'components/TextBox'
import AssignedItemsButton from 'components/AssignedItemsButton'
import { DialogModal } from 'components/DialogModal'

import { getCurrentVenueUuid } from 'store/Venues/selector'
import {
  getCurrentCategory,
  getIsCategoriesLoading,
} from 'store/Categories/selectors'
import {
  updateCategories,
  createCategory,
} from 'store/Categories/actions'
import { transformModifiersToUpdateInCategory } from 'utils/modifiers/transformModifiersToUpdateInCategory'

import { updateGlobalItem } from 'store/Items/actions'
import {
  defaultSelectedItem,
  modifiersMappingMinMaxOrderCount,
} from 'utils/modifiers/modifiers'
import { updatedItemsWithDefaultSelectedItem } from 'utils/modifiers/updatedItemsWithDefaultSelectedItem'
import {
  getItems,
  getAssignedItemsToCurrentModifierGroup,
  getCategoriesUuids,
  getIsItemsLoading,
} from 'store/Items/selector'

const ModifierGroupsForm = () => {
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false)
  const [showValidationErrors, setShowValidationErrors] =
    useState(false)
  const [formStep, setFormStep] = useState(1)

  const history = useHistory()
  const { state: urlQueryParamsState } = useLocation()

  const dispatch = useDispatch()

  const modifierGroupUuid = urlQueryParamsState?.modifierGroupUuid
  const isNewModifierGroup = modifierGroupUuid === 'new'

  const isCategoriesLoading = useSelector(getIsCategoriesLoading)
  const isItemsLoading = useSelector(getIsItemsLoading)
  const isLoading = isCategoriesLoading || isItemsLoading

  const venueUuid = useSelector(getCurrentVenueUuid)
  const currentModifier = useSelector((state) =>
    getCurrentCategory(state, modifierGroupUuid)
  )
  const displayCategoriesUuids = useSelector(getCategoriesUuids)
  const allItems = useSelector(getItems)
  const assignedItems = useSelector((state) =>
    getAssignedItemsToCurrentModifierGroup(state, modifierGroupUuid)
  )

  const alphabeticallySortedItems = orderBy(allItems, 'name')

  const defualtSelectedItem = useMemo(() => {
    const selectedItem = alphabeticallySortedItems.find(
      (item) =>
        item?.itemCategoryPreferences?.[modifierGroupUuid]
          ?.defaultModifier === true
    )
    return selectedItem ?? ''
  }, [alphabeticallySortedItems, modifierGroupUuid])

  const initialValues = {
    canopyDisplayName: currentModifier?.canopyDisplayName ?? '',
    name: currentModifier?.name ?? '',
    items: assignedItems ?? [],
    defaultSelectedUuid: defualtSelectedItem.uuid,
    categoryDisplay: currentModifier?.categoryDisplay ?? true,
    modifiersMappingMinMaxOrderCount:
      modifiersMappingMinMaxOrderCount(alphabeticallySortedItems),
  }

  const [defaultSelectedItems, setDefaultSelectedItems] = useState(
    initialValues.items ?? []
  )

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    canopyDisplayName: Yup.string().required('Required'),
    items: Yup.array().min(1).required('Required'),
  })

  const disableUpdateButton = (dirty, errors) => {
    const disableButton =
      !dirty || (showValidationErrors && !isEmpty(errors))

    return disableButton
  }

  const modalBody = (
    <div className="dialog-modal__body-text">
      Are you sure? Archiving this Modifier Group will deactivate it
      and cannot be reserved.
    </div>
  )

  const onCloseModal = () =>
    history.push({ state: { modifierGroupUuid: null } })

  const goBack = () => setFormStep(1)

  const modalTitle = isNewModifierGroup
    ? 'Add Modifier Group'
    : 'Edit Modifier Group'

  const modalStepTitle = formStep === 1 ? modalTitle : 'Modifiers'

  const handleSaveAssignedItems = (items) => {
    setDefaultSelectedItems(items)
  }

  const onClickArchiveButton = (e) => {
    e.preventDefault()
    setIsArchiveModalOpen(true)
  }

  const onClickArchiveModalConfirmation = () => {
    const date = new Date().toISOString()
    const params = [
      {
        ...currentModifier,
        deletedAt: date,
      },
    ]
    dispatch(updateCategories(params)).then((response) => {
      // if action payload is empty, then stay on the same page
      if (isEmpty(response?.payload)) return

      history.push({ state: { modifierGroupUuid: null } })
    })
  }

  const onSubmit = (values) => {
    const {
      name,
      canopyDisplayName,
      defaultSelectedUuid,
      categoryDisplay,
    } = values

    const createModifierGroupData = [
      {
        name,
        categoryDisplay,
        canopyDisplayName,
        uuid: uuid(),
        venueUuid,
        type: 'ModifierItemCategory',
        productType: 'Food',
      },
    ]

    const updateModifierGroupData = [
      {
        ...currentModifier,
        name,
        canopyDisplayName,
      },
    ]

    const updatedItems = transformModifiersToUpdateInCategory(
      initialValues?.items,
      initialValues?.modifiersMappingMinMaxOrderCount,
      allItems,
      values?.items,
      modifierGroupUuid,
      values?.modifiersMappingMinMaxOrderCount,
      displayCategoriesUuids
    )

    const defaultSelectedItemData = defaultSelectedItem(
      allItems,
      initialValues.defaultSelectedUuid,
      defaultSelectedUuid,
      modifierGroupUuid
    )

    // return items with default selected items without duplicates
    const itemsAndDefaultSelectedItemsWithoutDuplicates =
      updatedItemsWithDefaultSelectedItem(
        updatedItems,
        defaultSelectedItemData,
        modifierGroupUuid
      )

    // orderMaxCount and orderMinCount are not needed in update request anymore
    // because min and max are set in Item Drawer in Modifier Groups drawer
    const itemsWithDeletedMinMax =
      itemsAndDefaultSelectedItemsWithoutDuplicates.map((item) => {
        const { orderMaxCount, orderMinCount, ...rest } = item
        return rest
      })

    if (isNewModifierGroup) {
      dispatch(createCategory(createModifierGroupData))
        .then((res) => {
          const newItems = transformModifiersToUpdateInCategory(
            initialValues?.items,
            initialValues?.modifiersMappingMinMaxOrderCount,
            allItems,
            values?.items,
            res?.payload[0]?.uuid,
            values?.modifiersMappingMinMaxOrderCount,
            displayCategoriesUuids
          )

          const newItemParams = newItems.map((item) => {
            return { ...item, modifier: true }
          })

          const defaultSelectedNewItemData = defaultSelectedItem(
            allItems,
            initialValues.defaultSelectedUuid,
            defaultSelectedUuid,
            res?.payload[0]?.uuid
          )
          const itemsAndDefaultSelectedItemsWithoutDuplicates =
            updatedItemsWithDefaultSelectedItem(
              newItemParams,
              defaultSelectedNewItemData
            )

          // orderMaxCount and orderMinCount are not needed in update request anymore
          // because min and max are set in Item Drawer in Modifier Groups drawer
          const itemsWithDeletedMinMax =
            itemsAndDefaultSelectedItemsWithoutDuplicates.map(
              (item) => {
                const { orderMaxCount, orderMinCount, ...rest } = item
                return rest
              }
            )

          if (
            !isEmpty(itemsAndDefaultSelectedItemsWithoutDuplicates)
          ) {
            return dispatch(updateGlobalItem(itemsWithDeletedMinMax))
          }
        })
        .then(() => {
          history.push({ state: { modifierGroupUuid: null } })
        })
    } else {
      dispatch(updateCategories(updateModifierGroupData))
        .then(() => {
          if (
            !isEmpty(itemsAndDefaultSelectedItemsWithoutDuplicates)
          ) {
            return dispatch(updateGlobalItem(itemsWithDeletedMinMax))
          }
        })
        .then(() => {
          history.push({ state: { modifierGroupUuid: null } })
        })
    }
  }

  return (
    <Modal
      title={modalStepTitle}
      onClose={onCloseModal}
      className="-narrow"
      isReturnIcon={formStep === 2}
      onClickReturn={goBack}
      onRequestClose={onCloseModal}
      isLoading={isLoading}
    >
      <div className="modal-body">
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ dirty, errors, values, setFieldValue }) => (
            <Form>
              {formStep === 1 && (
                <div className="item-settings">
                  <div className="margin-bottom-base item-settings__help-text">
                    <span className="color-danger">*</span> indicates
                    a required field
                  </div>
                  <h3 className="header-title">Info</h3>
                  <div className="item-settings__section">
                    <Field
                      component={TextBox}
                      label="Name"
                      name="canopyDisplayName"
                      showValidationErrors={showValidationErrors}
                      required
                    />
                  </div>
                  <div className="item-settings__section">
                    <Field
                      component={TextBox}
                      label="Display name"
                      name="name"
                      showValidationErrors={showValidationErrors}
                      required
                    />
                  </div>
                  <div>
                    <h3 className="header-title">Modifiers</h3>
                    <div className="item-settings__section -with-border-bottom">
                      <AssignedItemsButton
                        label="Select Modifiers"
                        assignedItems={values?.items?.length}
                        handleClick={() => setFormStep(2)}
                        required
                        error={
                          !isEmpty(errors?.items) &&
                          showValidationErrors
                        }
                        key={values?.items?.length}
                      />
                    </div>
                  </div>

                  {/* <section className="item-settings__archive-button-wrapper">
                <button
                  className="btn -outline -block "
                  onClick={onClickArchiveButton}
                  type="button"
                >
                  Archive
                </button>
              </section> */}
                  <DialogModal
                    closeModalCallback={setIsArchiveModalOpen}
                    confirmationCallback={
                      onClickArchiveModalConfirmation
                    }
                    modalBody={modalBody}
                    modalIsOpen={isArchiveModalOpen}
                    modalTitle="Archive Modifier Group"
                    modalButtonText="CONFIRM"
                  />
                  <div className="btn-wrapper">
                    <button
                      className="btn -update"
                      onClick={() => setShowValidationErrors(true)}
                      disabled={disableUpdateButton(dirty, errors)}
                      type="submit"
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}
              {formStep === 2 && (
                <Modifiers
                  handleSaveAssignedItems={handleSaveAssignedItems}
                  items={alphabeticallySortedItems}
                  goBack={goBack}
                  defaultSelectedItemsUuids={defaultSelectedItems}
                  defaultSelectedUuid={values?.defaultSelectedUuid}
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

ModifierGroupsForm.propTypes = {}

export default ModifierGroupsForm
